import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { Buffer } from 'buffer';
import {
    AxisScrollStrategies,
    emptyLine,
    emptyFill,
    lightningChart,
    LUT,
    PalettedFill,
    AxisTickStrategies,
    SolidFill,
    SolidLine,
    ColorRGBA,
    FontSettings,
    Themes
} from "@lightningchart/lcjs";
import DetectionServices from "../../services/api/DetectionServices";
import { useAuthContext } from '../../dataproviders/AuthDataProvider';
import { useDetectionContext } from "../../dataproviders/DetectionDataProvider";

const DetectionSpectrogram = ({ detectionParam }) => {
    const authContext = useAuthContext();
    const [updateHeatmapChart, setUpdateHeatmapChart] = useState(false);
    const detectionContext = useDetectionContext();
    const loadingDetectionRef = useRef(false);
    const audioContextRef = useRef(null);
    const [chart, setChart] = useState(undefined);
    const id = 'detection-spectrogram-chart';
    const newMarkersRef = useRef([]);

    // #region ----- LightningChart const -----
    const maxFreq = 24000;
    const lutCETD1 = useMemo(() => new LUT({
        steps: [
            { value: 0, color: ColorRGBA(197, 197, 197) },
            { value: 5, color: ColorRGBA(195, 195, 195) },
            { value: 10, color: ColorRGBA(193, 193, 193) },
            { value: 15, color: ColorRGBA(192, 192, 192) },
            { value: 20, color: ColorRGBA(190, 190, 190) },
            { value: 25, color: ColorRGBA(189, 189, 189) },
            { value: 30, color: ColorRGBA(187, 187, 187) },
            { value: 35, color: ColorRGBA(186, 186, 186) },
            { value: 40, color: ColorRGBA(184, 184, 184) },
            { value: 45, color: ColorRGBA(182, 182, 182) },
            { value: 50, color: ColorRGBA(181, 181, 181) },
            { value: 55, color: ColorRGBA(179, 179, 179) },
            { value: 60, color: ColorRGBA(178, 178, 178) },
            { value: 65, color: ColorRGBA(176, 176, 176) },
            { value: 70, color: ColorRGBA(175, 175, 175) },
            { value: 75, color: ColorRGBA(173, 173, 173) },
            { value: 80, color: ColorRGBA(172, 172, 172) },
            { value: 85, color: ColorRGBA(170, 170, 170) },
            { value: 90, color: ColorRGBA(168, 168, 168) },
            { value: 95, color: ColorRGBA(166, 166, 166) },
            { value: 100, color: ColorRGBA(164, 164, 164) },
            { value: 105, color: ColorRGBA(162, 162, 162) },
            { value: 110, color: ColorRGBA(161, 161, 161) },
            { value: 115, color: ColorRGBA(159, 159, 159) },
            { value: 120, color: ColorRGBA(157, 157, 157) },
            { value: 125, color: ColorRGBA(155, 155, 155) },
            { value: 130, color: ColorRGBA(153, 153, 153) },
            { value: 135, color: ColorRGBA(151, 151, 151) },
            { value: 140, color: ColorRGBA(150, 150, 150) },
            { value: 145, color: ColorRGBA(148, 148, 148) },
            { value: 150, color: ColorRGBA(146, 146, 146) },
            { value: 155, color: ColorRGBA(144, 144, 144) },
            { value: 160, color: ColorRGBA(142, 142, 142) },
            { value: 165, color: ColorRGBA(140, 140, 140) },
            { value: 170, color: ColorRGBA(139, 139, 139) },
            { value: 175, color: ColorRGBA(137, 137, 137) },
            { value: 180, color: ColorRGBA(135, 135, 135) },
            { value: 185, color: ColorRGBA(133, 133, 133) },
            { value: 190, color: ColorRGBA(131, 131, 131) },
            { value: 195, color: ColorRGBA(129, 129, 129) },
            { value: 200, color: ColorRGBA(128, 128, 128) },
            { value: 205, color: ColorRGBA(126, 126, 126) },
            { value: 210, color: ColorRGBA(124, 124, 124) },
            { value: 215, color: ColorRGBA(122, 122, 122) },
            { value: 220, color: ColorRGBA(120, 120, 120) },
            { value: 225, color: ColorRGBA(118, 118, 118) },
            { value: 230, color: ColorRGBA(117, 117, 117) },
            { value: 235, color: ColorRGBA(115, 115, 115) },
            { value: 240, color: ColorRGBA(113, 113, 113) },
            { value: 245, color: ColorRGBA(111, 111, 111) },
            { value: 250, color: ColorRGBA(109, 109, 109) },
            { value: 255, color: ColorRGBA(108, 108, 108) },
        ],
        units: "dB",
        interpolate: true,
    }), []);
    const lutCETD2 = useMemo(() => new LUT({
        steps: [
            { value: 0, color: ColorRGBA(122, 98, 33) },
            { value: 5, color: ColorRGBA(115, 97, 40) },
            { value: 10, color: ColorRGBA(109, 96, 48) },
            { value: 15, color: ColorRGBA(102, 96, 55) },
            { value: 20, color: ColorRGBA(96, 95, 63) },
            { value: 25, color: ColorRGBA(89, 94, 70) },
            { value: 30, color: ColorRGBA(83, 94, 78) },
            { value: 35, color: ColorRGBA(77, 93, 86) },
            { value: 40, color: ColorRGBA(70, 92, 93) },
            { value: 45, color: ColorRGBA(64, 92, 101) },
            { value: 50, color: ColorRGBA(57, 91, 108) },
            { value: 55, color: ColorRGBA(51, 90, 116) },
            { value: 60, color: ColorRGBA(45, 90, 124) },
            { value: 65, color: ColorRGBA(45, 89, 127) },
            { value: 70, color: ColorRGBA(46, 89, 130) },
            { value: 75, color: ColorRGBA(46, 89, 134) },
            { value: 80, color: ColorRGBA(47, 89, 137) },
            { value: 85, color: ColorRGBA(47, 89, 140) },
            { value: 90, color: ColorRGBA(48, 89, 144) },
            { value: 95, color: ColorRGBA(48, 89, 147) },
            { value: 100, color: ColorRGBA(49, 89, 151) },
            { value: 105, color: ColorRGBA(49, 89, 154) },
            { value: 110, color: ColorRGBA(50, 89, 157) },
            { value: 115, color: ColorRGBA(50, 89, 161) },
            { value: 120, color: ColorRGBA(51, 89, 164) },
            { value: 125, color: ColorRGBA(52, 89, 168) },
            { value: 130, color: ColorRGBA(55, 90, 171) },
            { value: 135, color: ColorRGBA(59, 91, 174) },
            { value: 140, color: ColorRGBA(63, 92, 178) },
            { value: 145, color: ColorRGBA(66, 93, 181) },
            { value: 150, color: ColorRGBA(70, 94, 184) },
            { value: 155, color: ColorRGBA(74, 95, 188) },
            { value: 160, color: ColorRGBA(78, 96, 191) },
            { value: 165, color: ColorRGBA(81, 97, 194) },
            { value: 170, color: ColorRGBA(85, 98, 198) },
            { value: 175, color: ColorRGBA(89, 99, 201) },
            { value: 180, color: ColorRGBA(93, 100, 204) },
            { value: 185, color: ColorRGBA(96, 101, 208) },
            { value: 190, color: ColorRGBA(100, 103, 211) },
            { value: 195, color: ColorRGBA(104, 104, 214) },
            { value: 200, color: ColorRGBA(107, 105, 218) },
            { value: 205, color: ColorRGBA(111, 106, 221) },
            { value: 210, color: ColorRGBA(115, 107, 224) },
            { value: 215, color: ColorRGBA(119, 108, 228) },
            { value: 220, color: ColorRGBA(122, 109, 231) },
            { value: 225, color: ColorRGBA(126, 110, 234) },
            { value: 230, color: ColorRGBA(130, 111, 238) },
            { value: 235, color: ColorRGBA(134, 112, 241) },
            { value: 240, color: ColorRGBA(137, 113, 244) },
            { value: 245, color: ColorRGBA(141, 114, 248) },
            { value: 250, color: ColorRGBA(145, 115, 251) },
            { value: 255, color: ColorRGBA(149, 117, 255) },
        ],
        units: "dB",
        interpolate: true,
    }), []);
    const lutCETD3 = useMemo(() => new LUT({
        steps: [
            { value: 0, color: ColorRGBA(131, 42, 7) },
            { value: 5, color: ColorRGBA(134, 45, 7) },
            { value: 10, color: ColorRGBA(138, 49, 7) },
            { value: 15, color: ColorRGBA(141, 52, 8) },
            { value: 20, color: ColorRGBA(145, 56, 8) },
            { value: 25, color: ColorRGBA(149, 60, 9) },
            { value: 30, color: ColorRGBA(152, 63, 9) },
            { value: 35, color: ColorRGBA(156, 67, 10) },
            { value: 40, color: ColorRGBA(160, 71, 10) },
            { value: 45, color: ColorRGBA(163, 74, 10) },
            { value: 50, color: ColorRGBA(167, 78, 11) },
            { value: 55, color: ColorRGBA(170, 81, 11) },
            { value: 60, color: ColorRGBA(174, 85, 12) },
            { value: 65, color: ColorRGBA(178, 89, 12) },
            { value: 70, color: ColorRGBA(181, 92, 13) },
            { value: 75, color: ColorRGBA(185, 96, 13) },
            { value: 80, color: ColorRGBA(189, 122, 14) },
            { value: 85, color: ColorRGBA(184, 121, 17) },
            { value: 90, color: ColorRGBA(179, 121, 21) },
            { value: 95, color: ColorRGBA(175, 121, 24) },
            { value: 100, color: ColorRGBA(170, 121, 28) },
            { value: 105, color: ColorRGBA(166, 121, 32) },
            { value: 110, color: ColorRGBA(161, 121, 35) },
            { value: 115, color: ColorRGBA(157, 121, 39) },
            { value: 120, color: ColorRGBA(152, 120, 43) },
            { value: 125, color: ColorRGBA(148, 120, 46) },
            { value: 130, color: ColorRGBA(143, 120, 50) },
            { value: 135, color: ColorRGBA(139, 120, 53) },
            { value: 140, color: ColorRGBA(134, 120, 57) },
            { value: 145, color: ColorRGBA(130, 120, 61) },
            { value: 150, color: ColorRGBA(125, 120, 64) },
            { value: 155, color: ColorRGBA(121, 119, 68) },
            { value: 160, color: ColorRGBA(116, 119, 72) },
            { value: 165, color: ColorRGBA(112, 119, 75) },
            { value: 170, color: ColorRGBA(107, 119, 79) },
            { value: 175, color: ColorRGBA(103, 119, 82) },
            { value: 180, color: ColorRGBA(98, 119, 86) },
            { value: 185, color: ColorRGBA(94, 119, 90) },
            { value: 190, color: ColorRGBA(89, 118, 93) },
            { value: 195, color: ColorRGBA(85, 118, 97) },
            { value: 200, color: ColorRGBA(80, 118, 101) },
            { value: 205, color: ColorRGBA(76, 118, 104) },
            { value: 210, color: ColorRGBA(71, 118, 108) },
            { value: 215, color: ColorRGBA(67, 118, 111) },
            { value: 220, color: ColorRGBA(62, 118, 115) },
            { value: 225, color: ColorRGBA(58, 117, 119) },
            { value: 230, color: ColorRGBA(53, 117, 122) },
            { value: 235, color: ColorRGBA(49, 117, 126) },
            { value: 240, color: ColorRGBA(44, 117, 130) },
            { value: 245, color: ColorRGBA(40, 117, 133) },
            { value: 250, color: ColorRGBA(35, 117, 137) },
            { value: 255, color: ColorRGBA(31, 117, 141) },
        ],
        units: "dB",
        interpolate: true,
    }), []);
    const lutCETD4 = useMemo(() => new LUT({
        steps: [
            { value: 0, color: ColorRGBA(34, 21, 18) },
            { value: 5, color: ColorRGBA(34, 22, 22) },
            { value: 10, color: ColorRGBA(34, 24, 26) },
            { value: 15, color: ColorRGBA(34, 26, 30) },
            { value: 20, color: ColorRGBA(34, 27, 34) },
            { value: 25, color: ColorRGBA(34, 29, 38) },
            { value: 30, color: ColorRGBA(34, 31, 42) },
            { value: 35, color: ColorRGBA(34, 32, 46) },
            { value: 40, color: ColorRGBA(34, 34, 50) },
            { value: 45, color: ColorRGBA(34, 36, 54) },
            { value: 50, color: ColorRGBA(34, 37, 58) },
            { value: 55, color: ColorRGBA(34, 39, 62) },
            { value: 60, color: ColorRGBA(35, 41, 66) },
            { value: 65, color: ColorRGBA(34, 43, 72) },
            { value: 70, color: ColorRGBA(34, 46, 79) },
            { value: 75, color: ColorRGBA(33, 49, 86) },
            { value: 80, color: ColorRGBA(33, 52, 92) },
            { value: 85, color: ColorRGBA(32, 55, 99) },
            { value: 90, color: ColorRGBA(32, 58, 106) },
            { value: 95, color: ColorRGBA(31, 60, 112) },
            { value: 100, color: ColorRGBA(31, 63, 119) },
            { value: 105, color: ColorRGBA(30, 66, 126) },
            { value: 110, color: ColorRGBA(30, 69, 132) },
            { value: 115, color: ColorRGBA(29, 72, 139) },
            { value: 120, color: ColorRGBA(29, 75, 146) },
            { value: 125, color: ColorRGBA(29, 78, 153) },
            { value: 130, color: ColorRGBA(32, 80, 156) },
            { value: 135, color: ColorRGBA(36, 83, 160) },
            { value: 140, color: ColorRGBA(39, 85, 164) },
            { value: 145, color: ColorRGBA(43, 88, 167) },
            { value: 150, color: ColorRGBA(46, 91, 171) },
            { value: 155, color: ColorRGBA(50, 93, 175) },
            { value: 160, color: ColorRGBA(54, 96, 178) },
            { value: 165, color: ColorRGBA(57, 98, 182) },
            { value: 170, color: ColorRGBA(61, 101, 186) },
            { value: 175, color: ColorRGBA(64, 104, 189) },
            { value: 180, color: ColorRGBA(68, 106, 193) },
            { value: 185, color: ColorRGBA(71, 109, 197) },
            { value: 190, color: ColorRGBA(75, 112, 201) },
            { value: 195, color: ColorRGBA(79, 114, 204) },
            { value: 200, color: ColorRGBA(82, 117, 208) },
            { value: 205, color: ColorRGBA(86, 119, 212) },
            { value: 210, color: ColorRGBA(89, 122, 215) },
            { value: 215, color: ColorRGBA(93, 125, 219) },
            { value: 220, color: ColorRGBA(96, 127, 223) },
            { value: 225, color: ColorRGBA(100, 130, 226) },
            { value: 230, color: ColorRGBA(104, 132, 230) },
            { value: 235, color: ColorRGBA(107, 135, 234) },
            { value: 240, color: ColorRGBA(111, 138, 237) },
            { value: 245, color: ColorRGBA(114, 140, 241) },
            { value: 250, color: ColorRGBA(118, 143, 245) },
            { value: 255, color: ColorRGBA(122, 146, 249) },
        ],
        units: "dB",
        interpolate: true,
    }), []);

    // #endregion
    // #region ----- Chart Spectrogram methods to process wave form -----
    const config = {
        /**
         * The resolution of the FFT calculations
         * Higher value means higher resolution decibel domain..
         */
        fftResolution: 8192, //[32, 32768, 4096, *lastchange-8192*, 12288]
        /**
         * Smoothing value for FFT calculations
         */
        smoothingTimeConstant: 0.1,
        /**
         * The size of processing buffer,
         * determines how often FFT is run
         */
        processorBufferSize: 2048, //FFT size -> 11000Hz
    };

    let license = {};

    const _auth = JSON.parse(sessionStorage.getItem("auth"));

    if (!!_auth) {
        if (_auth.license !== undefined && _auth.license !== 'undefined' && _auth.license !== '' &&
            _auth.apptitle !== undefined && _auth.apptitle !== 'undefined' && _auth.apptitle !== '' &&
            _auth.company !== undefined && _auth.company !== 'undefined' && _auth.company !== '') {
            license = {
                license: _auth.license,
                licenseInformation: {
                    appTitle: _auth.apptitle,
                    company: _auth.company
                }
            }
        }
    }

    /**
     * Create data matrix for heatmap from one dimensional array
     * @param {Uint8Array}  data        FFT Data
     * @param {number}      strideSize  Single data block width
     * @param {number}      tickCount   Data row count
     */
    const remapDataToTwoDimensionalMatrix = useCallback((
        data: Uint8Array,
        strideSize: number,
        tickCount: number
    ) => {
        // Map the one dimensional data to two dimensional data
        const arr = Array.from(data);
        const output = Array.from(Array(tickCount)).map(() =>
            Array.from(Array(strideSize))
        );
        for (let col = 0; col < tickCount; col += 1) {
            for (let row = 0; row < strideSize; row += 1) {
                output[col][row] = arr[col * strideSize + row];
            }
        }
        return output;
    }, []);

    /**
     * Process a AudioBuffer and create FFT Data for Spectrogram from it.
     * @param   {AudioBuffer}     audioBuffer   AudioBuffer to process into FFT data.
     * @returns {WaveFormData}                  Processed data
     */
    const processWaveForm = useCallback(async (audioBuffer) => {
        // Create a new OfflineAudioContext with information from the pre-created audioBuffer
        const offlineCtx = new OfflineAudioContext(
            audioBuffer.numberOfChannels,
            audioBuffer.length,
            audioBuffer.sampleRate
        );

        // Pre-calculate values used multiple times
        const channelCount = audioBuffer.numberOfChannels;
        const fftSize = config.fftResolution;
        const fftHalfSize = fftSize / 2;
        const totalSamples = audioBuffer.length;

        const processorSize = config.processorBufferSize;
        const smoothingConstant = config.smoothingTimeConstant;

        // Calculate buffer size once
        const bufferLength = Math.ceil(totalSamples / processorSize) * fftHalfSize;

        // Create buffer source and set the buffer
        const source = offlineCtx.createBufferSource();
        source.buffer = audioBuffer;

        // Create splitter for multi-channel processing
        const splitter = offlineCtx.createChannelSplitter(channelCount);

        // General analyzer for overall audio analysis
        const generalAnalyzer = offlineCtx.createAnalyser();
        generalAnalyzer.fftSize = fftSize;
        generalAnalyzer.smoothingTimeConstant = smoothingConstant;

        // Pre-allocate all channel FFT data buffers at once with correct size
        const channelFFtDataBuffers = Array.from(
            { length: channelCount },
            () => new Uint8Array(bufferLength)
        );

        // Create and configure all analyzers at once
        const analyzers = Array.from({ length: channelCount }, () => {
            const analyzer = offlineCtx.createAnalyser();
            analyzer.smoothingTimeConstant = smoothingConstant;
            analyzer.fftSize = fftSize;
            return analyzer;
        });

        // Connect all analyzers to their respective channels
        analyzers.forEach((analyzer, i) => {
            splitter.connect(analyzer, i);
        });

        // Create processor for FFT data collection
        const processor = offlineCtx.createScriptProcessor
            ? offlineCtx.createScriptProcessor(processorSize, 1, 1)
            : offlineCtx.createJavaScriptNode(processorSize, 1, 1);

        let offset = 0;
        //let tickIndex = 0; // Define globally

        // Use a more efficient audio processing function
        processor.onaudioprocess = () => {
            const freqBinCount = generalAnalyzer.frequencyBinCount;
            const endOffset = offset + freqBinCount;

            for (let i = 0; i < channelCount; i++) {
                if (endOffset <= channelFFtDataBuffers[i].length) {
                    const bufferSlice = channelFFtDataBuffers[i].subarray(offset, endOffset);
                    analyzers[i].getByteFrequencyData(bufferSlice);

                    //// Logging (limit to first 5 ticks and first 20 bins)
                    //if (tickIndex < 5) {
                    //    console.log(`Tick ${tickIndex}, Channel ${i}`);
                    //    for (let bin = 0; bin < Math.min(20, bufferSlice.length); bin++) {
                    //        const byteValue = bufferSlice[bin]; // 0�255
                    //        const db = (byteValue / 255) * (analyzers[i].maxDecibels - analyzers[i].minDecibels) + analyzers[i].minDecibels;

                    //        console.log(`Bin ${bin}: dB=${db.toFixed(2)}, Byte=${byteValue}`);
                    //    }
                    //}
                }
            }

            offset += freqBinCount;
            //tickIndex++;
        };

        // Connect audio nodes
        source.connect(splitter);
        source.connect(processor);
        processor.connect(offlineCtx.destination);
        source.connect(generalAnalyzer);

        // Start processing
        source.start(0);

        // Wait for rendering to complete
        await offlineCtx.startRendering();

        // Calculate result values once
        const result = {
            channels: channelFFtDataBuffers.map(buffer => buffer.slice()),
            channelDbRanges: analyzers.map(analyzer => ({
                minDecibels: analyzer.minDecibels,
                maxDecibels: analyzer.maxDecibels,
            })),
            stride: fftHalfSize,
            tickCount: Math.ceil(totalSamples / processorSize),
            maxFreq: offlineCtx.sampleRate / 2,
            duration: audioBuffer.duration,
        };

        // Cleanup connections
        source.disconnect();
        processor.disconnect();
        splitter.disconnect();
        generalAnalyzer.disconnect();
        analyzers.forEach(analyzer => analyzer.disconnect());

        // Clear references for GC
        source.buffer = null;
        processor.onaudioprocess = null;

        // Return result
        return result;
    }, []);

    // #endregion

    const handleError = (errorType, errorMessage) => {
        authContext.HandleError(errorType, errorMessage);
    }

    useEffect(() => {
        if (updateHeatmapChart) {
            if (chart && chart.scrollingHeatmapRef !== null && chart.scrollingHeatmapRef !== undefined) {
                let selectedLut;

                if (detectionContext.SelectedOption === 'CET-D1') {
                    selectedLut = lutCETD1;
                }
                else if (detectionContext.SelectedOption === 'CET-D2') {
                    selectedLut = lutCETD2;
                }
                else if (detectionContext.SelectedOption === 'CET-D3') {
                    selectedLut = lutCETD3;
                }
                else if (detectionContext.SelectedOption === 'CET-D4') {
                    selectedLut = lutCETD4;
                }

                chart.scrollingHeatmapRef.setFillStyle(new PalettedFill({ lut: selectedLut }));
            }
        }

    }, [updateHeatmapChart, detectionContext.SelectedOption, chart]);

    const getStatus = useCallback(() => {
        let status = 2;

        if (detectionContext) {
            if (detectionContext.SelectedFilter === "Unvalidated") {
                status = 0;
            }
            else if (detectionContext.SelectedFilter === "Validated") {
                status = 1;
            }
            else {
                status = 2;
            }
        }
        return status;
    }, [detectionContext]);

    // Create chart just once during lifecycle of component.
    useEffect(() => {
        let newId = getSpectrogramId();
        const container = document.getElementById(newId);
        if (!container) return

        const lc = lightningChart(license)
        // #region ----- Chart Spectrogram primary config -----
        let chartRef = lc.ChartXY({
            theme: Themes.light,
            container
        })
        .setTitle("Spectrogram")
        .setBackgroundFillStyle(new SolidFill({ color: ColorRGBA(255, 255, 255) }))
        .setSeriesBackgroundStrokeStyle(new SolidLine({
            thickness: 0
        }))
        .setPadding({ left: 10, right: 20, top: 10, bottom: 0 });
        // #endregion
        // #region ----- Chart Spectrogram extra config -----
        chartRef
            //.setMouseInteractionRectangleFit(false) - to support rectangle reset zoom
            //.setMouseInteractionRectangleZoom(false) - to support rectangle zoom
            .setMouseInteractionWheelZoom(false)
            //.setMouseInteractionPan(false)
            .setTitleFillStyle(emptyFill);

        //x-axis
        chartRef
            .getDefaultAxisX()
            .setAxisInteractionZoomByDragging(false)
            .setAxisInteractionPanByDragging(false)
            .setAxisInteractionZoomByDragging(false)
            .setAxisInteractionReleaseByDoubleClicking(false)
            .setScrollStrategy(AxisScrollStrategies.progressive)
            .setTickStrategy(AxisTickStrategies.Time, (timeTicks) => timeTicks
                //Configure TimeTickStrategy
                .setMajorTickStyle((majorTicks) => majorTicks
                    .setLabelFont(new FontSettings({ size: 11 }))
                )
            )

        //y-axis
        chartRef
            .getDefaultAxisY()
            .setAxisInteractionZoomByDragging(false)
            .setAxisInteractionPanByDragging(false)
            .setAxisInteractionZoomByDragging(false)
            .setAxisInteractionReleaseByDoubleClicking(false)
            .setTickStrategy(AxisTickStrategies.Numeric, (strategy) => strategy
                // Configure NumericTickStrategy
                .setMajorTickStyle((tickStyle: VisibleTicks) => tickStyle
                    .setLabelFont(new FontSettings({ size: 11 }))
                )
            ).setTitle('Frequency (Hz)').setTitleFont(new FontSettings({ size: 11 }));

        //cursor
        chartRef.setCursor((cursor) =>
            cursor.setTickMarkerXVisible(false).setTickMarkerYVisible(false)
        );

        chartRef
            .setCursorFormatting((chart, hit) => [
                ['Y', '', `${hit.axisY.formatValue(hit.y)}`],
                ['Intensity', '', `${(typeof hit.intensity === 'undefined' || hit.intensity === null || isNaN(hit.intensity)) ? 0 : hit.intensity.toFixed(0)}`]
            ]);

        // #endregion

        // #region ----- Chart Spectrogram Scrolling heatmap -----
        let scrollingHeatmapRef = chartRef
            .addHeatmapScrollingGridSeries({
                heatmapDataType: 'intensity',
                scrollDimension: 'columns',
                resolution: (maxFreq / 5)
            })
            .setDataCleaning({ minDataPointCount: 1 })
            .setFillStyle(new PalettedFill({ lut: lutCETD1 }))
            .setWireframeStyle(emptyLine)
            .setEffect(false);
        // #endregion

        setChart({ chartRef, scrollingHeatmapRef });
        return () => {
            // Destroy chart when component lifecycle ends.
            lc.dispose();
        };

    }, [id]);

    const getSpectrogram = useCallback(async (hasAudioData, audioData, loadingElement, loadingNAElement, divDetectionChart, divDetectionNAChart, updateDetectionLoading, isBase64, setPointFill) => {

        let newId = getSpectrogramId();
        //#region append again if it is null
        if ((typeof divDetectionChart === 'undefined' || divDetectionChart === null)) {
            divDetectionChart = document.getElementById(newId);
        }
        if ((typeof divDetectionNAChart === 'undefined' || divDetectionNAChart === null)) {
            divDetectionNAChart = getNAContainer();
        }
        //#endregion

        if (!(typeof audioData === 'undefined' || audioData === null)) {

            hasAudioData = true;

            // Process the message within the allocated time frame (500ms)
            // Example: console.log(message) or call your processing function here
            // Assuming you have the byte array in a string format
            let byteString;
            let buffer;
            let arrayBuffer;
            let blob;
            let isBlob = false;

            if (isBase64) {
                byteString = audioData; // your byte array string here

                // Convert the byte string to an ArrayBuffer
                buffer = Buffer.from(byteString, 'base64');
                arrayBuffer = new Uint8Array(buffer).buffer;

                // Create a Blob from the ArrayBuffer
                blob = new Blob([arrayBuffer]);
                isBlob = true;
            }
            else if (audioData instanceof Blob) {
                blob = audioData;
                isBlob = true;
            }
            else {
                blob = audioData;
            }

            //check if not empty size
            if (isBlob) {
                if (blob.size > 0) {
                    loadingElement.classList.remove('loading'); // Remove the class "loading"
                    loadingElement.textContent = 'Spectrogram is rendering. Please wait...'; // Set the text to "Spectrogram is rendering. Please wait..."

                    //console.log(message.timestamp);
                    let fileReader = new FileReader();

                    fileReader.onload = async () => {
                        let arrayBuffer = fileReader.result;
                        try {
                            let audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);
                            //// Assuming you already have the AudioBuffer (from decodeAudioData)
                            //const channelData = audioBuffer.getChannelData(0); // First channel (Float32Array)
                            //console.log('Client PCM Samples (first 100):');
                            //for (let i = 0; i < 100; i++) {
                            //    console.log(`Sample ${i}: ${channelData[i].toFixed(8)}`);
                            //}

                            // Process the loaded wave form to prepare it for being added to the chart
                            let processed = await processWaveForm(audioBuffer);
                            //console.log("Client processed result:", JSON.stringify({
                            //    channels: processed.channels.map(ch => ch.slice(0, 100)), // Limit large arrays for display
                            //    channelDbRanges: processed.channelDbRanges,
                            //    stride: processed.stride,
                            //    tickCount: processed.tickCount,
                            //    maxFreq: processed.maxFreq,
                            //    duration: processed.duration,
                            //    length: processed.channels[0].length
                            //}));

                            // NOTE: Example only for 1st channel.
                            let chData2D = remapDataToTwoDimensionalMatrix(
                                processed.channels[0],
                                processed.stride,
                                processed.tickCount
                            );
                            //console.log("Client FFT channel length:", processed.channels[0].length);
                            //console.log("Client FFT Matrix:", JSON.stringify(chData2D.slice(0, 50))); // First 50 frames as example

                            renderSpectrogram(chData2D, arrayBuffer, loadingElement, loadingNAElement, divDetectionChart, updateDetectionLoading, setPointFill, undefined);

                        } catch (error) {
                            console.error('Error decoding audio data:', error);
                        }
                    };

                    fileReader.onerror = (error) => {
                        console.error('Error reading blob:', error);
                    };

                    fileReader.readAsArrayBuffer(blob);

                    // Clear the buffers
                    audioData = undefined;
                    byteString = undefined;
                    buffer = undefined;
                    arrayBuffer = undefined;
                    blob = undefined;
                }
                else {
                    hasAudioData = false;
                }
            }
            else {
                hasAudioData = false;
            }
        }
        else {
            hasAudioData = false;
        }

        if (!hasAudioData) {
            // Find the existing canvas element and temporarily hide it
            let canvasElement = divDetectionChart.querySelector('canvas');
            if (canvasElement) {
                canvasElement.style.display = 'none'; // Temporarily hide the canvas
            }

            // Append the "Spectrogram not available" message to the existing content
            divDetectionChart.insertAdjacentHTML('beforeend', `
                                <div class="spectrogram-not-available">
                                    <span>Spectrogram is not available. Please try again later.</span>
                                    <button class="ms-1 cursor-pointer" onclick="document.getElementById('btnRefresh').click();">
                                        <i class="fa-solid fa-rotate-right"></i>
                                    </button>
                                </div>
                            `);
        }

        return hasAudioData;

    }, [chart]);

    const renderSpectrogram = useCallback(async (chData2D, arrayBuffer, loadingElement, loadingNAElement, divDetectionChart, updateDetectionLoading, setPointFill, chDataPoint) => {
        let sampleCount = 12; //2 seconds = 512;//2 minutes = 8.533333
        let frameCount = 500;
        let newFrame = frameCount / sampleCount;

        chart.scrollingHeatmapRef
            .setStart({ x: 0, y: 0 })
            .setStep({ x: newFrame, y: 5.5 });

        chart.scrollingHeatmapRef.clear();
        chart.scrollingHeatmapRef.addIntensityValues(chData2D);

        //x-axis
        chart.chartRef
            .getDefaultAxisX()
            .setInterval({ start: 0, end: (chData2D.length / sampleCount * frameCount), stopAxisAfter: false });

        // Ensure disposal of existing markers before proceeding
        Promise.resolve().then(() => {
            if (newMarkersRef.current && newMarkersRef.current.length > 0) {
                newMarkersRef.current.forEach((marker) => {
                    marker.dispose();
                });
                // Clear the markers array after disposal
                newMarkersRef.current.length = 0;
            }

        }).then(() => {
            setUpdateHeatmapChart(true);


            // Clear the buffers
            arrayBuffer = undefined;
            //audioBuffer = undefined;
            //processed = undefined;
            chData2D = undefined;
            //fileReader = undefined;

            //#region Render spectrogram complete
            // Remove loading indicator
            if (loadingElement) loadingElement.remove();
            if (loadingNAElement) loadingNAElement.remove();

            if (!(typeof divDetectionChart === 'undefined' || divDetectionChart === null)) {
                let canvasElement = divDetectionChart.querySelector('canvas');
                if (canvasElement) {
                    canvasElement.style.display = 'block'; // Show the canvas again
                }
            }

            if (detectionContext && updateDetectionLoading) detectionContext.SetDetectionLoading(false);
            loadingDetectionRef.current = false;

            const _auth = JSON.parse(sessionStorage.getItem("auth"));

            if (!!_auth) {
                if (_auth.license !== undefined && _auth.license !== 'undefined' && _auth.license !== '') {

                    const inputString = _auth.license;
                    if (!(inputString === null || inputString === undefined)) {
                        const parts = inputString.split('-');
                        if (parts.length === 3) {
                            const licenseKey = parts[2]; // This will give you the third part after splitting
                            const canvasElements = document.querySelectorAll('[data-lcjs-id="' + licenseKey + '"]');

                            canvasElements.forEach(canvasElement => {
                                canvasElement.remove(); // Removes each matched element from the DOM
                            });
                        }
                    }
                }
            }
            //#endregion
        });
    }, [chart])

    const getDetectionDetail = useCallback(async () => {
        let isExist = (detectionParam);

        if (!chart || !isExist || !chart.chartRef || chart.chartRef.isDisposed()) return;

        if (!loadingDetectionRef.current) {
            loadingDetectionRef.current = true;

            //console.log('call getDetectionDetail', rowIndex, rowId, useId);
            let divDetectionNAChart = getNAContainerQuerySelector();
            let newId = getSpectrogramId();
            let divDetectionChart = document.getElementById(newId);
            let loadingElement;
            let loadingNAElement;
            let hasData = false;
            let hasAudioData = false;

            setUpdateHeatmapChart(false);
            if (detectionContext) detectionContext.SetDetectionLoading(true);
            // Disable the button and add the loading class
            if (!(typeof divDetectionChart === 'undefined' || divDetectionChart === null)) {
                // Find the element with the class 'spectrogram-not-available'
                let spectrogramNotAvailableElement = divDetectionChart.querySelector('.spectrogram-not-available');

                // If the element exists, remove it
                if (spectrogramNotAvailableElement) {
                    spectrogramNotAvailableElement.remove();
                }

                // Find the existing canvas element and temporarily hide it
                let canvasElement = divDetectionChart.querySelector('canvas');
                if (canvasElement) {
                    canvasElement.style.display = 'none'; // Temporarily hide the canvas
                }

                // Add the loading indicator without overwriting the existing content
                loadingElement = document.createElement('div');
                loadingElement.classList.add('loading');
                divDetectionChart.appendChild(loadingElement); // Append the loading element
            }

            if (!(typeof divDetectionNAChart === 'undefined' || divDetectionNAChart === null)) {
                divDetectionNAChart.innerHTML = '<div class="loading"></div>';
                loadingNAElement = divDetectionNAChart.querySelector('.loading');
            }

            const req = {
                rowId: detectionParam.rowId,
                rowIndex: detectionParam.rowIndex,
                status: getStatus(),
                useId: detectionParam.useId,
                clusterId: detectionParam.clusterId,
                useClusterId: detectionParam.useClusterId,
                startDate: detectionParam.startDate ? detectionParam.startDate : '',
                endDate: detectionParam.endDate ? detectionParam.endDate : '',
                projectId: detectionContext.DetectionConfig.projectid,
                vesselId: detectionContext.DetectionConfig.vesselid,
                deviceId: detectionContext.DetectionConfig.deviceid
            };

            try {
                let raw = await DetectionServices.GetDetectionDetails(req);

                if (raw) {
                    let data = raw.data;

                    if (data && !(typeof data.rowIndex === 'undefined' || data.rowIndex === null)) {
                        hasData = true;

                        //clear the validation status
                        if (detectionContext) {
                            detectionContext.SetCurrentValidationStatus('');
                            detectionContext.SetDetection(data);
                            detectionContext.SetDetectionIndex(parseInt(data.rowIndex));
                            detectionContext.SetDetectionCount(parseInt(data.rowCount));
                        }

                        hasAudioData = await getSpectrogram(hasAudioData, data.audioData, loadingElement, loadingNAElement, divDetectionChart, divDetectionNAChart, true, true, false);

                        if (detectionContext) {
                            const convertedStartDate = new Date(data.startDate);
                            detectionContext.SetDetectionStartDate(data.startDate ? convertedStartDate : '');

                            const convertedEndDate = new Date(data.endDate);
                            detectionContext.SetDetectionEndDate(data.endDate ? convertedEndDate : '');
                        }

                        data = undefined;
                    }
                    else {
                        hasData = false;
                        //handleError("Warning", "Record not found");
                        if (detectionContext) detectionContext.SetSelectedDetectionDateRange([null, null]);
                    }

                    raw = undefined;
                }
            }
            catch (e) {
                // Remove loading indicator
                if (loadingElement) loadingElement.remove();
                if (loadingNAElement) loadingNAElement.remove();
                handleError('Error', 'Error occurred while retrieving data. Please try again.');
                console.error('Error retrieving detection details:', e);
            }
            finally {
                processFinalizeDetectionDetail(hasData, hasAudioData, loadingElement, loadingNAElement, divDetectionNAChart, divDetectionChart);
            }
        }

    }, [chart, detectionParam, id, detectionContext]);

    const processFinalizeDetectionDetail = useCallback((hasData, hasAudioData, loadingElement, loadingNAElement, divDetectionNAChart, divDetectionChart) => {
        if ((!hasData && !(typeof divDetectionNAChart === 'undefined' || divDetectionNAChart === null)) ||
            (!hasAudioData && !(typeof divDetectionChart === 'undefined' || divDetectionChart === null))) {
            // Remove loading indicator
            if (loadingElement) loadingElement.remove();
            if (loadingNAElement) loadingNAElement.remove();
            if (!hasData && !(typeof divDetectionNAChart === 'undefined' || divDetectionNAChart === null)) {
                divDetectionNAChart.innerHTML = 'Detection is not available at this moment.';
            }
            else if (hasAudioData && !(typeof divDetectionChart === 'undefined' || divDetectionChart === null)) {
                let canvasElement = divDetectionChart.querySelector('canvas');
                if (canvasElement) {
                    canvasElement.style.display = 'block'; // Show the canvas again
                }
            }

            if (detectionContext) detectionContext.SetDetectionLoading(false);
            loadingDetectionRef.current = false;

            const _auth = JSON.parse(sessionStorage.getItem("auth"));

            if (!!_auth) {
                if (_auth.license !== undefined && _auth.license !== 'undefined' && _auth.license !== '') {

                    const inputString = _auth.license;
                    if (!(inputString === null || inputString === undefined)) {
                        const parts = inputString.split('-');
                        if (parts.length === 3) {
                            const licenseKey = parts[2]; // This will give you the third part after splitting
                            const canvasElements = document.querySelectorAll('[data-lcjs-id="' + licenseKey + '"]');

                            canvasElements.forEach(canvasElement => {
                                canvasElement.remove(); // Removes each matched element from the DOM
                            });
                        }
                    }
                }
            }
        }

        detectionContext.SetChartHasAudioData(hasAudioData);
    }, [detectionContext]);

    useEffect(() => {
        if (audioContextRef.current === null || audioContextRef.current === undefined) {
            audioContextRef.current = new AudioContext();
        }

        if (detectionParam !== null) {
            getDetectionDetail();
        }

        return () => {
            if (audioContextRef.current !== null && audioContextRef.current !== undefined) {
                audioContextRef.current.close();
                audioContextRef.current = undefined;
            }
        };

    }, [detectionParam]);

    const getSpectrogramId = useCallback(() => {
        return id;
    }, [id]);

    const getNAContainer = useCallback(() => {
        return document.getElementById('detection-na-container');
    }, []);

    const getNAContainerQuerySelector = useCallback(() => {
        return document.querySelector('#detection-na-container');
    }, []);

    return (
        <>
            <div id={id} className="detection-spectrogram-container ms-1 me-1 mb-1"></div>
        </>
    );
};

export default DetectionSpectrogram;
